import React, {useEffect, useRef, useState} from 'react';
import Button from '../../../../../incase-domain/components/button/Button';
import {SORT} from '../../../../../incase-domain/common/sort.enum';
import '../../../../../styles/components/genericTable.scss';
import {Column} from '../DataView';

interface TableProps<T> {
    columns: Column<T>[];
    rows: T[];
    extraRow?: Record<string, JSX.Element>;
    rowIdentifier: (row: T) => string | number;
    loading?: boolean;
    onRowClick?: (row: T) => void;
    sortBy?: Record<string, SORT>;
    onSortChange?: (sortBy: Record<string, SORT>) => void;
    selectedRows?: {[key: string | number]: T};
    hideHeader?: boolean;
    enableSelect?: boolean;
    onSelectedItem?: (item: T) => void;
    onSelectAll?: () => void;
}

function useLoadingProgress(active: boolean | undefined): number | null {
    const [progress, setProgress] = useState<number | null>(null);
    const progressRef = useRef<number | null>(null);

    useEffect(() => {
        if (active) {
            progressRef.current = 0;
            setProgress(0);

            const timer = window.setInterval(() => {
                const current = progressRef.current ?? 0;
                const next = Math.min(90, current + Math.max(0.8, (90 - current) * 0.1));
                progressRef.current = next;
                setProgress(next);
            }, 80);

            return () => window.clearInterval(timer);
        }

        if (progressRef.current == null) {
            return () => {
                // no-op
            };
        }

        progressRef.current = 100;
        setProgress(100);

        const hide = window.setTimeout(() => {
            progressRef.current = null;
            setProgress(null);
        }, 450);

        return () => window.clearTimeout(hide);
    }, [active]);

    return progress;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function Table<T extends Record<string, any> | null>(props: TableProps<T>): JSX.Element {
    const {
        columns,
        rows,
        extraRow,
        rowIdentifier,
        loading,
        onRowClick,
        sortBy = {},
        onSortChange,
        hideHeader,
        onSelectedItem,
        enableSelect,
        selectedRows,
        onSelectAll,
    } = props;

    function findColumnByKey(columnKey: string): Column<T> | undefined {
        return columns.find((el) => el.key === columnKey);
    }

    const loadingProgress = useLoadingProgress(loading);

    function numberOfSelectedRows(): number {
        return selectedRows ? Object.keys(selectedRows).length : 0;
    }

    function isSelectAllChecked(): boolean {
        return numberOfSelectedRows() === rows.length && rows.length > 0;
    }

    function nextSort(columnKey: string): void {
        const column = findColumnByKey(columnKey);

        if (!column || !column.sortable) {
            // eslint-disable-next-line no-console
            console.error(`Column with key ${columnKey} does not exist or is not sortable`);
            return;
        }

        let nextSortValue = SORT.NEUTRAL;

        if (!sortBy) {
            nextSortValue = SORT.DESC;
        } else {
            switch (sortBy[columnKey]) {
                case undefined:
                    nextSortValue = SORT.DESC;
                    break;
                case SORT.NEUTRAL:
                    nextSortValue = SORT.DESC;
                    break;
                case SORT.ASC:
                    nextSortValue = SORT.NEUTRAL;
                    break;
                case SORT.DESC:
                    nextSortValue = SORT.ASC;
                    break;
                default:
                    nextSortValue = SORT.NEUTRAL;
                    break;
            }
        }

        onSortChange?.({[columnKey]: nextSortValue});
    }

    function sortIconByColumn(columnKey: string): string {
        const column = findColumnByKey(columnKey);

        if (!column || !column.sortable) {
            // eslint-disable-next-line no-console
            console.error(`Column with key ${columnKey} does not exist or is not sortable`);
            return '';
        }

        if (!sortBy) {
            return 'solar:sort-vertical-linear';
        }

        const sort: SORT = sortBy[columnKey];

        switch (sort) {
            case SORT.NEUTRAL:
                return 'solar:sort-vertical-linear';
            case SORT.ASC:
                return 'solar:arrow-up-linear';
            case SORT.DESC:
                return 'solar:arrow-down-linear';
            default:
                return 'solar:sort-vertical-linear';
        }
    }

    return (
        <div className="c-genericTable__shell">
            {loadingProgress != null && (
                <div
                    className={`c-genericTable__progress${hideHeader ? ' is-noHeader' : ''}`}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(loadingProgress)}
                >
                    <div className="c-genericTable__progress_fill" style={{width: `${loadingProgress}%`}} />
                </div>
            )}
            <table className="c-genericTable">
                <colgroup>
                    {enableSelect && <col key="col-select" className="c-genericTable__selectColumn" />}
                    {columns.map((column, index) => (
                        <col key={`col-${index}`} style={{width: column.width || 'auto'}} />
                    ))}
                </colgroup>

                {!hideHeader && (
                    <thead>
                        <tr>
                            {enableSelect && (
                                <th>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        square
                                        leading={isSelectAllChecked() ? 'solar:check-square-bold' : 'solar:stop-linear'}
                                        onClick={(e) => {
                                            e?.stopPropagation();
                                            onSelectAll?.();
                                        }}
                                    />
                                </th>
                            )}
                            {columns.map((column, index) => (
                                <th key={`th-${index}`}>
                                    <div className="c-genericTable__header_cell">
                                        {column.label}
                                        {column.key && column.sortable && (
                                            <Button
                                                square
                                                variant="ghost"
                                                size="xs"
                                                leading={sortIconByColumn(column.key)}
                                                onClick={() => column.key && nextSort(column.key)}
                                            />
                                        )}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                )}

                <tbody>
                    {rows.map((row, index) => (
                        <tr key={`rowd-${index}`} onClick={() => onRowClick?.(row)}>
                            {enableSelect && (
                                <td>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        square
                                        leading={selectedRows?.[rowIdentifier(row)] ? 'solar:check-square-bold' : 'solar:stop-linear'}
                                        onClick={(e) => {
                                            e?.stopPropagation();
                                            onSelectedItem?.(row);
                                        }}
                                    />
                                </td>
                            )}

                            {columns.map((column, colIndex) => (
                                <td key={`td-${colIndex}`}>
                                    <div className={`c-genericTable__cell c-genericTable__cell--${column.align}`}>
                                        {column.body && column.body(row)}
                                    </div>
                                </td>
                            ))}
                        </tr>
                    ))}
                    {extraRow && (
                        <tr>
                            {enableSelect && <td aria-hidden="true" />}
                            {columns.map((column, colIndex) => (
                                <td key={`td-extra-row-${colIndex}--${column.key}`}>
                                    <div className={`c-genericTable__cell c-genericTable__cell--${column.align}`}>
                                        {column.key ? extraRow[column.key] ?? null : null}
                                    </div>
                                </td>
                            ))}
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
}

export default Table;
